import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Checkbox,
  Skeleton,
  Snackbar,
  Alert,
} from '@mui/material';
import { neutral, warning } from '_lex/_foundations/phxColors';
import parse from 'html-react-parser';
import { useWrittenDirectivesContext } from '../context';
import { useParams } from 'react-router-dom';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import RightDrawer from '_lex/_layouts/TakeoverLayout/RightDrawer';
import { useStandardsContext } from '../../context';
import { translate as t } from 'hooks/useTranslations';
import useAccreditationStandardById from '../../../../_data_sources/AccreditationStandards/useStandard';
import { useNavigate } from 'react-router-dom';
import SearchResults from '../_components/SearchResults';
import _ from 'lodash';
import {
  createWrittenDirective,
  destroyWrittenDirective,
} from '../../../../_data_sources/WrittenDirectives/manageWrittenDirectives';
import { getIssuedManual } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { Policy, Section, SubSection } from 'views/App/Portal/Policy/_types/policy';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { AppContext } from 'views/App/context';

const WrittenDirectiveList: React.FC = () => {
  const { id: accreditationId, versionId, standardId, jurisdictionId } = useParams();
  const { currentOrganizationId, isFeatureEnabled, hasPermission } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData') || isFeatureEnabled('awbPremiumDemo');
  const {
    writtenDirectives,
    isLoading: isDirectivesLoading,
    jurisdiction,
    searchQuery,
    selectedItem,
    setSelectedItem,
    isContentLoading,
    setIsContentLoading,
    searchInputValue,
    setSearchInputValue,
  } = useWrittenDirectivesContext();
  const { version, isLoading: isVersionLoading } = useStandardsContext();
  const { data: standard, isLoading: isStandardLoading } = useAccreditationStandardById(
    currentOrganizationId,
    accreditationId || '',
    versionId || '',
    standardId || ''
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedManual, setSelectedManual] = useState<any | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [selectedPreface, setSelectedPreface] = useState<any | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [selectedContentUris, setSelectedContentUris] = useState<Set<string>>(new Set());
  const [localSearchValue, setLocalSearchValue] = useState(searchInputValue);
  const [savingWrittenDirective, setSavingWrittenDirective] = useState(false);

  const standardTitle = standard ? `${standard.heading} ${standard.title}` : '';
  const isLoading = isDirectivesLoading || isVersionLoading || isStandardLoading;

  const parseContentUri = (contentUri: string) => {
    const contentMatch = contentUri.match(/^xref:\/\/manuals?\/([^/]+)\/content\/([^/]+)$/);
    if (contentMatch) {
      return {
        manualId: contentMatch[1],
        contentId: contentMatch[2],
      };
    }
    return null;
  };

  const findPolicyBySectionId = (manualData: any, contentId: string): Policy | null => {
    for (const chapter of manualData.chapters || []) {
      for (const policy of chapter.policies || []) {
        // Check if policy ID matches
        if (policy.id === contentId) {
          return policy;
        }

        // Check sections and subsections recursively
        const checkSection = (section: any): boolean => {
          if (section.id === contentId) return true;
          return section.subSections?.some(checkSection) || false;
        };

        if (policy.sections?.some(checkSection)) {
          return policy;
        }
      }
    }
    return null;
  };

  const fetchManualData = async (item: any) => {
    setIsContentLoading(true);
    setSelectedPolicy(null);
    setSelectedPreface(null);
    const parsed = parseContentUri(item.contentUri);
    if (!parsed) {
      console.error('Invalid content URI');
      setIsContentLoading(false);
      return;
    }
    const { manualId, contentId } = parsed;

    try {
      const manualData = await getIssuedManual(manualId);
      const foundPolicy = findPolicyBySectionId(manualData, contentId);
      setSelectedManual(manualData);
      if (foundPolicy) {
        setSelectedPolicy(foundPolicy);
      } else {
        const foundPreface = manualData.prefaces?.find((p: any) => p.id === contentId);
        if (foundPreface) setSelectedPreface(foundPreface);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsContentLoading(false);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      fetchManualData(selectedItem);
    }
  }, [selectedItem]);

  const getContentUri = (contentUri: string, contentId: string) => {
    // Extract manualId from contentUri
    const match = contentUri.match(/xref:\/\/manuals\/([^/]+)/);
    if (!match) return contentUri;
    const manualId = match[1];
    return `xref://manuals/${manualId}/content/${contentId}`;
  };

  // Update selectedContentUris when writtenDirectives changes
  useEffect(() => {
    if (writtenDirectives) {
      const uris = new Set(writtenDirectives.map((d) => d.contentUri));
      setSelectedContentUris(uris);
    }
  }, [writtenDirectives]);

  const handleToggleDirective = async (contentUri: string, isChecked: boolean) => {
    if (!currentOrganizationId || !accreditationId || !versionId || !standardId || !jurisdictionId) return;

    // Check permissions before proceeding
    if (isChecked && !hasPermission('createWrittenDirective', currentOrganizationId)) {
      enqueueSnackbar(t('awb.standards.writtenDirectives.permissions.noCreatePermission'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      return;
    }

    if (!isChecked && !hasPermission('deleteWrittenDirective', currentOrganizationId)) {
      enqueueSnackbar(t('awb.standards.writtenDirectives.permissions.noDeletePermission'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      return;
    }

    // Update local state immediately for better UX
    setSelectedContentUris((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(contentUri);
      } else {
        newSet.delete(contentUri);
      }
      return newSet;
    });

    try {
      if (isChecked) {
        setSavingWrittenDirective(true);
        // Add new written directive
        await createWrittenDirective(
          currentOrganizationId,
          accreditationId,
          versionId,
          standardId,
          jurisdictionId,
          {
            contentUri,
          },
          undefined,
          undefined,
          useRUIData
        );
        // Invalidate the written directives query to trigger a refetch
        await queryClient.invalidateQueries([
          'written_directives',
          currentOrganizationId,
          accreditationId,
          versionId,
          standardId,
          jurisdictionId,
        ]);
        console.log('enqueueSnackbar', t('awb.standards.writtenDirectives.linkSuccess'));
        enqueueSnackbar(t('awb.standards.writtenDirectives.linkSuccess'), {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      } else {
        // Find and remove existing written directive
        const existingDirective = writtenDirectives?.find((d) => d.contentUri === contentUri);
        if (existingDirective) {
          await destroyWrittenDirective(
            currentOrganizationId,
            accreditationId,
            versionId,
            standardId,
            existingDirective.id
          );
          // Invalidate the written directives query to trigger a refetch
          await queryClient.invalidateQueries([
            'written_directives',
            currentOrganizationId,
            accreditationId,
            versionId,
            standardId,
            jurisdictionId,
          ]);
        }
      }
    } catch (error) {
      // Revert local state on error
      setSelectedContentUris((prev) => {
        const newSet = new Set(prev);
        if (isChecked) {
          newSet.delete(contentUri);
        } else {
          newSet.add(contentUri);
        }
        return newSet;
      });
      console.error('Error toggling directive:', error);
    } finally {
      setSavingWrittenDirective(false);
    }
  };

  const isContentUriLinked = (contentUri: string) => {
    return selectedContentUris.has(contentUri);
  };

  const scrollToSection = (section: any) => {
    let element = document.getElementById(`section_${section.id}`);
    if (!element) {
      element = document.getElementById(`preface_${section.id}`);
    }
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSectionId(section.id);
    }
  };

  const debouncedSetSearchInput = useCallback(
    _.debounce((value: string) => {
      if (value.length >= 3) {
        setSearchInputValue(value);
      } else {
        setSearchInputValue('');
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetSearchInput(localSearchValue);
    return () => {
      debouncedSetSearchInput.cancel();
    };
  }, [localSearchValue, debouncedSetSearchInput]);

  // console.log('selectedContentUris', selectedContentUris);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: selectedItem ? 'auto 200px 1fr' : 'auto 1fr',
          gap: 2,
          width: 750,
          maxWidth: '94%',
          margin: '0 auto',
          height: '100%',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: neutral[100],
            p: 4,
          }}
        >
          <TextField
            fullWidth
            label={false}
            variant='outlined'
            placeholder={t('awb.standards.writtenDirectives.searchPlaceholder')}
            value={localSearchValue}
            onChange={(e) => setLocalSearchValue(e.target.value)}
            size='small'
            sx={{
              pt: 0,
            }}
          />
          {searchQuery.isLoading && <CircularProgress size={24} />}
        </Box>

        {searchInputValue &&
          !searchQuery.isLoading &&
          !searchQuery.data?.pages?.[0]?.content?.length &&
          !selectedItem && (
            <Box sx={{ p: 2 }}>
              <Typography
                variant='body2'
                color='textSecondary'
              >
                No results found
              </Typography>
            </Box>
          )}

        {searchInputValue && !_.isEmpty(searchQuery?.data?.pages?.[0]?.content) && (
          <Box
            sx={{
              overflow: 'auto',
              border: `1px solid ${neutral[100]}`,
              backgroundColor: neutral[25],
              minHeight: 0,
            }}
          >
            <SearchResults />
          </Box>
        )}

        {(searchInputValue?.length || 0) < 3 && !selectedItem && (
          <Box
            sx={{
              overflow: 'auto',
              border: `1px solid ${neutral[100]}`,
              backgroundColor: neutral[25],
              minHeight: 0,
              p: 4,
              pt: 10,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Box
                component='img'
                src='/admin/img/empty-placeholder.png'
                alt='Search placeholder'
                sx={{
                  width: 200,
                  height: 'auto',
                }}
              />
              <Typography
                variant='bodyMd'
                sx={{
                  color: neutral[600],
                  maxWidth: 400,
                  mx: 'auto',
                  textAlign: 'center',
                }}
              >
                {t('awb.standards.writtenDirectives.searchInstructions')}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedItem && (
          <Box
            sx={{
              overflow: 'auto',
              border: `1px solid ${neutral[100]}`,
              backgroundColor: neutral[25],
              minHeight: 0,
              p: 4,
            }}
          >
            {isContentLoading ? (
              <Stack spacing={2}>
                <Skeleton
                  variant='rectangular'
                  height={28}
                  width='50%'
                />
                <Skeleton variant='text' />
                <Skeleton variant='text' />
                <Skeleton variant='text' />
              </Stack>
            ) : selectedPolicy ? (
              <Stack spacing={4}>
                <Typography variant='subtitleMd'>{selectedManual.name}</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                    <Checkbox
                      checked={isContentUriLinked(getContentUri(selectedItem.contentUri, selectedPolicy.id))}
                      onChange={(e) =>
                        handleToggleDirective(
                          getContentUri(selectedItem.contentUri, selectedPolicy.id),
                          e.target.checked
                        )
                      }
                      sx={{
                        p: 0,
                      }}
                    />
                    <Box>
                      <Typography variant='subtitleMd'>Policy {selectedPolicy.key}</Typography>
                      <Typography variant='displaySm'>{selectedPolicy.title}</Typography>
                    </Box>
                  </Box>
                </Stack>
                {selectedPolicy.sections.map((section) => {
                  const sectionContentUri = getContentUri(selectedItem.contentUri, section.id);
                  return (
                    <Box
                      key={section.id}
                      id={`section_${section.id}`}
                      sx={{
                        backgroundColor:
                          section.id === activeSectionId
                            ? warning[50]
                            : isContentUriLinked(sectionContentUri)
                              ? warning[100]
                              : 'transparent',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <Checkbox
                          checked={isContentUriLinked(sectionContentUri)}
                          onChange={(e) => handleToggleDirective(sectionContentUri, e.target.checked)}
                          sx={{
                            p: 0,
                          }}
                        />
                        <Box>
                          <Typography variant='h6'>{section.title}</Typography>
                          {section.content && (
                            <Typography
                              variant='body1'
                              sx={{ pl: 4 }}
                            >
                              {parse(section.content || '', {
                                replace(node: any, ind: number) {
                                  return PolicyContentHelper.handleContent(node, ind);
                                },
                              })}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {section.subSections?.map((sub) => {
                        const subContentUri = getContentUri(selectedItem.contentUri, sub.id);
                        return (
                          <Box
                            key={sub.id}
                            id={`section_${sub.id}`}
                            sx={{
                              ml: 4,
                              borderLeft: '2px solid #eee',
                              pl: 2,
                              backgroundColor:
                                sub.id === activeSectionId
                                  ? warning[50]
                                  : isContentUriLinked(subContentUri)
                                    ? warning[100]
                                    : 'transparent',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                              <Checkbox
                                checked={isContentUriLinked(subContentUri)}
                                onChange={(e) => handleToggleDirective(subContentUri, e.target.checked)}
                                sx={{
                                  p: 0,
                                }}
                              />
                              <Box>
                                <Typography variant='h6'>{sub.title}</Typography>
                                {sub.content && (
                                  <Typography
                                    variant='body1'
                                    sx={{ pl: 4 }}
                                  >
                                    {parse(sub.content || '', {
                                      replace(node: any, ind: number) {
                                        return PolicyContentHelper.handleContent(node, ind);
                                      },
                                    })}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Stack>
            ) : selectedPreface ? (
              <Stack spacing={4}>
                <Box
                  id={`preface_${selectedPreface.id}`}
                  sx={{
                    backgroundColor: selectedPreface.id === activeSectionId ? warning[50] : 'transparent',
                  }}
                >
                  <Typography variant='subtitleMd'>{selectedManual.title}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                    <Checkbox
                      checked={isContentUriLinked(selectedItem.contentUri)}
                      onChange={(e) => handleToggleDirective(selectedItem.contentUri, e.target.checked)}
                      sx={{
                        p: 0,
                      }}
                    />
                    <Box>
                      <Typography variant='h6'>{selectedPreface.title}</Typography>
                      {selectedPreface.content && (
                        <Typography
                          variant='body1'
                          sx={{ pl: 4 }}
                        >
                          {parse(selectedPreface.content || '', {
                            replace(node: any, ind: number) {
                              return PolicyContentHelper.handleContent(node, ind);
                            },
                          })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Stack>
            ) : (
              <Typography
                variant='body1'
                color='textSecondary'
              >
                Unable to load content details.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderRightDrawer = () => {
    return (
      <RightDrawer
        header={t('awb.standards.writtenDirectives.rightDrawer.title')}
        noPadding
        content={
          <Stack spacing={0}>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.jurisdiction')} />
              <CardContent>
                <Typography variant='bodySm'>{jurisdiction?.name || '-'}</Typography>
              </CardContent>
            </Card>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.writtenDirectives')} />
              <CardContent>
                <Stack spacing={2}>
                  {writtenDirectives?.map((directive) => (
                    <Box
                      key={directive.id}
                      sx={{
                        py: 1,
                        borderBottom: `1px solid ${neutral[100]}`,
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant='labelSm'
                        noWrap
                        sx={{
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                        onClick={() =>
                          navigate(
                            `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${jurisdictionId}/written-directives/${directive.id}`
                          )
                        }
                      >
                        {directive.contentHeading} {directive.contentTitle}
                      </Typography>
                      <Typography
                        variant='bodySm'
                        noWrap
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {directive.manualTitle}
                      </Typography>
                    </Box>
                  ))}
                  {savingWrittenDirective && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.description')} />
              <CardContent>
                <Box sx={{ maxHeight: '300px', overflow: 'auto', minWidth: 0, whiteSpace: 'normal' }}>
                  <Typography
                    component='div'
                    variant='bodySm'
                  >
                    {parse(standard?.publishedContent || '-')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        }
        open={true}
      />
    );
  };

  return (
    <TakeoverLayout
      pageTitle={isLoading ? '' : `${standardTitle}: ${t('awb.standards.writtenDirectives.title')}`}
      backUrl={`/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}`}
      rightDrawer={renderRightDrawer()}
      contentHeaderRight={
        <Button
          variant='contained'
          size='small'
          onClick={() =>
            navigate(`/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}`)
          }
        >
          Done
        </Button>
      }
      contentBackground='neutral'
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default WrittenDirectiveList;
